import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Mail, RefreshCw } from "lucide-react";
import { NETWORKS } from "@/data/networks";
import { Seo } from "@/components/hv/Seo";
import { FadeIn } from "@/components/hv/Motion";
import { PasswordGate, useUnlocked } from "@/components/hv/PasswordGate";
import { SettingsPanel } from "@/components/hv/SettingsPanel";
import { SharePreview } from "@/components/hv/SharePreview";
import { PartnersPanel } from "@/components/hv/PartnersPanel";
import { LeadsPanel } from "@/components/hv/LeadsPanel";
import { TOUR_VIDEO } from "@/data/networks";

const API = process.env.REACT_APP_BACKEND_URL;
const LABELS = { hub: "Hub (all channels)", facebook: "Facebook", youtube: "YouTube", instagram: "Instagram", linkedin: "LinkedIn" };
const ACCENTS = Object.fromEntries(NETWORKS.map((n) => [n.key, n.accent]));

const Stat = ({ label, value, sub, testId }) => (
  <div className="rounded-2xl border border-[#E8DDD3] bg-white p-6" data-testid={testId}>
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">{label}</div>
    <div className="mt-2 font-serif text-4xl tracking-tight text-[#1F1A17]">{value}</div>
    {sub && <div className="mt-1 text-xs text-[#685F58]">{sub}</div>}
  </div>
);

const Dashboard = ({ pw, onLock }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [digest, setDigest] = useState("idle");
  const [digestMsg, setDigestMsg] = useState("");

  const sendDigest = async () => {
    setDigest("sending"); setDigestMsg("");
    const r = await fetch(`${API}/api/stats/send-digest`, { method: "POST", headers: { "X-Stats-Key": pw } });
    const body = await r.json().catch(() => ({}));
    setDigestMsg(r.ok ? `Sent to ${body.to}.` : body.detail || "Could not send the digest.");
    setDigest("idle");
  };

  const load = async () => {
    setLoading(true);
    const r = await fetch(`${API}/api/stats?days=${days}`, { headers: { "X-Stats-Key": pw } });
    if (r.status === 401) return onLock();
    setData(await r.json());
    setLoading(false);
  };
  useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="py-40 text-center text-sm text-[#685F58]" data-testid="stats-loading">Loading…</div>;
  const t = data.totals;
  const maxViews = Math.max(1, ...data.networks.map((n) => n.page_view));
  const rate = t.page_view ? ((t.signup_click / t.page_view) * 100).toFixed(1) : "0.0";

  return (
    <div data-testid="stats-dashboard">
    <FadeIn className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#B4532A]">Hearthside · private</div>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight text-[#1F1A17]">Who comes, and <em className="text-[#B4532A]">who signs up.</em></h1>
          <p className="mt-3 text-sm text-[#685F58]">Cookie-free counts, stored on our own server. Your own visits are excluded while you're signed in here.</p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`stats-range-${d}`}
              className={`h-10 px-4 rounded-full text-sm transition-colors duration-300 ${days === d ? "bg-[#1F1A17] text-[#FBF8F4]" : "border border-[#E8DDD3] bg-white text-[#685F58] hover:text-[#1F1A17]"}`}>
              {d}d
            </button>
          ))}
          <button onClick={load} aria-label="Refresh" data-testid="stats-refresh" className="h-10 w-10 rounded-full border border-[#E8DDD3] bg-white grid place-items-center text-[#1F1A17]">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat label="Page views" value={t.page_view} testId="stat-total-views" />
        <Stat label="Signup clicks" value={t.signup_click} sub={`${rate}% of views`} testId="stat-total-signups" />
        <Stat label="Follow clicks" value={t.follow_click} testId="stat-total-follows" />
        <Stat label="Video plays" value={t.video_play} testId="stat-total-videos" />
        <Stat label="Tour leads" value={t.leads} sub={`${t.checklist_print} checklist prints`} testId="stat-total-leads" />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="stats-actions">
        <a href={`${API}/api/stats/export.csv?days=${days}&key=${encodeURIComponent(pw)}`} data-testid="stats-export-csv" className="inline-flex items-center gap-2 rounded-full border border-[#E8DDD3] bg-white h-10 px-4 text-sm text-[#1F1A17] hover:border-[#1F1A17] transition-colors">
          <Download className="h-4 w-4" /> Export stats CSV
        </a>
        <a href={`${API}/api/stats/leads.csv?key=${encodeURIComponent(pw)}`} data-testid="stats-export-leads" className="inline-flex items-center gap-2 rounded-full border border-[#E8DDD3] bg-white h-10 px-4 text-sm text-[#1F1A17] hover:border-[#1F1A17] transition-colors">
          <Download className="h-4 w-4" /> Export leads CSV
        </a>
        <button onClick={sendDigest} disabled={digest === "sending"} data-testid="stats-send-digest" className="inline-flex items-center gap-2 rounded-full bg-[#1F1A17] hover:bg-[#B4532A] disabled:opacity-60 h-10 px-4 text-sm text-[#FBF8F4] transition-colors">
          <Mail className="h-4 w-4" /> {digest === "sending" ? "Sending…" : "Email me this week's digest"}
        </button>
        {digestMsg && <span className="text-sm text-[#685F58]" data-testid="stats-digest-msg">{digestMsg}</span>}
      </div>

      <div className="mt-12 rounded-[2rem] border border-[#E8DDD3] bg-white overflow-hidden" data-testid="stats-network-table">
        <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr] gap-3 px-6 py-4 border-b border-[#E8DDD3] font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">
          <span>Network</span><span className="text-right">Views</span><span className="text-right">Signups</span><span className="text-right">Follows</span><span className="text-right">Rate</span>
        </div>
        {[...data.networks].sort((a, b) => b.signup_click - a.signup_click || b.page_view - a.page_view).map((n) => (
          <div key={n.network} className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr] gap-3 items-center px-6 py-5 border-b border-[#E8DDD3] last:border-0" data-testid={`stats-row-${n.network}`}>
            <span>
              <span className="flex items-center gap-2 text-sm font-medium text-[#1F1A17]">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: ACCENTS[n.network] || "#B4532A" }} />{LABELS[n.network]}
              </span>
              <span className="mt-2 block h-1.5 rounded-full bg-[#F3ECE5] overflow-hidden">
                <span className="block h-full rounded-full" style={{ width: `${(n.page_view / maxViews) * 100}%`, background: ACCENTS[n.network] || "#B4532A" }} />
              </span>
            </span>
            <span className="text-right font-serif text-2xl text-[#1F1A17]">{n.page_view}</span>
            <span className="text-right font-serif text-2xl text-[#1F1A17]">{n.signup_click}</span>
            <span className="text-right font-serif text-2xl text-[#1F1A17]">{n.follow_click}</span>
            <span className="text-right font-mono text-sm text-[#685F58]">{n.signup_rate}%</span>
          </div>
        ))}
      </div>

      <div className="mt-12 grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="rounded-[2rem] border border-[#E8DDD3] bg-white p-6 sm:p-8" data-testid="stats-daily">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">Views by day</div>
          {data.daily.length === 0 ? (
            <p className="mt-6 text-sm text-[#685F58]">No visits yet in this window.</p>
          ) : (
            <div className="mt-6 flex items-end gap-1 h-40">
              {data.daily.map((d) => {
                const m = Math.max(1, ...data.daily.map((x) => x.page_view));
                return (
                  <div key={d.day} className="flex-1 flex flex-col justify-end gap-0.5 group relative" title={`${d.day}: ${d.page_view} views, ${d.signup_click} signups`}>
                    <div className="rounded-t bg-[#B4532A]" style={{ height: `${(d.signup_click / m) * 100}%`, minHeight: d.signup_click ? 3 : 0 }} />
                    <div className="rounded-t bg-[#1F1A17]/80" style={{ height: `${(d.page_view / m) * 100}%`, minHeight: 2 }} />
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-4 flex gap-5 text-xs text-[#685F58]">
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-[#1F1A17]/80" /> Views</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-[#B4532A]" /> Signup clicks</span>
          </div>
        </div>
        <div className="rounded-[2rem] border border-[#E8DDD3] bg-white p-6 sm:p-8" data-testid="stats-referrers">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">Arrived from</div>
          {data.referrers.length === 0 ? (
            <p className="mt-6 text-sm text-[#685F58]">No referrer data yet. Direct visits and in-app browsers often send none.</p>
          ) : (
            <ul className="mt-6 space-y-3">
              {data.referrers.map((r) => (
                <li key={r.host} className="flex items-center justify-between text-sm">
                  <span className="text-[#1F1A17]">{r.host}</span>
                  <span className="font-mono text-[#685F58]">{r.n}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="rounded-[2rem] border border-[#E8DDD3] bg-white p-6 sm:p-8" data-testid="stats-posts">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">Which posts convert</div>
          <p className="mt-2 text-xs text-[#685F58]">Add <code className="font-mono">utm_content=your-post-id</code> to each social link. Visits, signup clicks and leads are grouped by it here and passed through to hearthvault.family.</p>
          {data.posts.length === 0 ? (
            <p className="mt-6 text-sm text-[#685F58]">No post-level data yet.</p>
          ) : (
            <table className="mt-5 w-full text-sm">
              <thead><tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]"><th className="text-left py-2 font-normal">Post</th><th className="text-left py-2 font-normal">Network</th><th className="text-right py-2 font-normal">Views</th><th className="text-right py-2 font-normal">Signups</th><th className="text-right py-2 font-normal">Leads</th></tr></thead>
              <tbody>
                {data.posts.map((p) => (
                  <tr key={`${p.content}-${p.network}`} className="border-t border-[#E8DDD3]" data-testid={`stats-post-${p.content}`}>
                    <td className="py-3 font-medium text-[#1F1A17]">{p.content}</td>
                    <td className="py-3 text-[#685F58]">{LABELS[p.network]}</td>
                    <td className="py-3 text-right font-serif text-lg text-[#1F1A17]">{p.page_view}</td>
                    <td className="py-3 text-right font-serif text-lg text-[#1F1A17]">{p.signup_click}</td>
                    <td className="py-3 text-right font-serif text-lg text-[#1F1A17]">{p.lead}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="rounded-[2rem] border border-[#E8DDD3] bg-white p-6 sm:p-8" data-testid="stats-chapters">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">Video: where people press play</div>
          {data.chapters.length === 0 ? (
            <p className="mt-6 text-sm text-[#685F58]">No video plays yet.</p>
          ) : (
            <ul className="mt-6 space-y-4">
              {data.chapters.map((c) => {
                const m = Math.max(1, ...data.chapters.map((x) => x.n));
                return (
                  <li key={c.chapter} data-testid={`stats-chapter-${c.chapter}`}>
                    <div className="flex items-center justify-between text-sm"><span className="text-[#1F1A17]">{c.chapter}</span><span className="font-mono text-[#685F58]">{c.n}</span></div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-[#F3ECE5] overflow-hidden"><div className="h-full rounded-full bg-[#DC2626]" style={{ width: `${(c.n / m) * 100}%` }} /></div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <LeadsPanel pw={pw} videoSet={!TOUR_VIDEO.youtubeId.startsWith("REPLACE")} />
      <PartnersPanel pw={pw} />
      <SettingsPanel pw={pw} />
      <SharePreview />

      <Link to="/kit" data-testid="stats-to-kit" className="mt-10 mr-6 inline-block text-sm text-[#685F58] hover:text-[#1F1A17] underline underline-offset-4">Open launch kit</Link>
      <button onClick={onLock} data-testid="stats-lock-btn" className="mt-10 text-sm text-[#685F58] hover:text-[#1F1A17] underline underline-offset-4">Lock stats</button>
    </FadeIn>
    </div>
  );
};

export default function StatsPage() {
  const { pw, setPw, lock } = useUnlocked();
  return (
    <div data-testid="stats-page" className="min-h-[60vh]">
      <Seo title="Hearthside stats (private)" description="Private analytics for HearthVault social landing pages." noindex />
      {pw ? <Dashboard pw={pw} onLock={lock} /> : (
        <PasswordGate title="Hearthside stats" description="Private. Enter the password to see visits and signup clicks by network." onUnlock={setPw} />
      )}
    </div>
  );
}
